/**
 * A number that matters.
 *
 * Extracted because there were already two copies disagreeing with each other:
 * the import summary rendered value-then-label with a thousands separator, the
 * campaign report label-then-value with a raw integer. Both are `.oc-metric`,
 * and side by side they would read as two different components.
 *
 * Value first is the correct order — `.oc-metric` is a flex column and the
 * design gives the value headline type, so leading with the label buries the
 * thing being read. Separators throughout, because six figures without them is
 * a number people have to count.
 */

import { Link } from "react-router-dom";

interface MetricProps {
  label: string;
  value: number;
  /** Renders in the danger colour. For counts that are bad news when non-zero. */
  negative?: boolean;
  /** Sits under the value in smaller type — a share, a caveat, a comparison. */
  note?: string;
  /**
   * Makes the whole metric a link — for a count that can be drilled into.
   *
   * Only pass one when the destination really holds this many rows. A metric
   * that reads 40 and opens a list of 12 is worse than one that does not open
   * at all, because it makes the reader distrust both numbers.
   */
  to?: string;
}

export function Metric({ label, value, negative, note, to }: MetricProps) {
  const body = (
    <>
      <span className={`oc-metric__value${negative ? " oc-metric__value--negative" : ""}`}>
        {value.toLocaleString()}
      </span>
      <span className="oc-label">{label}</span>
      {note && <span className="oc-body-sm oc-text-secondary">{note}</span>}
    </>
  );

  if (to) {
    return (
      <Link className="oc-metric oc-metric--link" to={to}>
        {body}
      </Link>
    );
  }

  return <div className="oc-metric">{body}</div>;
}

/** A share of a total, for the `note` slot. Guards the zero case. */
export function percentOf(value: number, total: number): string {
  if (total <= 0) return "—";
  return `${((value / total) * 100).toFixed(1)}%`;
}
